import { useDroppable } from "@dnd-kit/core";
import { Box, Breadcrumbs, Link, Skeleton, Typography } from "@mui/material";
import type { ReactNode } from "react";
import { Link as RouterLink } from "react-router-dom";
import type { DocumentsPageBreadcrumbItem } from "src/utils/documentsBreadcrumbs";

import type { DocumentsTableMoveTarget } from "./DocumentsTable.types";
import {
  buildDocumentsTableMovePlan,
  getDocumentsTableBreadcrumbFolderDropTargetId,
  getDocumentsTableBreadcrumbRootDropTargetId,
} from "./TableDnD/DocumentsTable.dnd";
import type { DocumentsTableMoveController } from "./TableDnD/useDocumentsTableMoveController";

type DocumentsPageBreadcrumbProps = {
  disabled?: boolean;
  isLoading?: boolean;
  items: DocumentsPageBreadcrumbItem[];
  moveController: DocumentsTableMoveController;
};

type DocumentsBreadcrumbDropTargetProps = {
  children: ReactNode;
  disabled: boolean;
  folderId: string | null;
  moveController: DocumentsTableMoveController;
};

const getBreadcrumbMoveTarget = (
  folderId: string | null
): DocumentsTableMoveTarget =>
  folderId === null ? { type: "root" } : { type: "folder", folderId };

const getBreadcrumbDropTargetId = (folderId: string | null) =>
  folderId === null
    ? getDocumentsTableBreadcrumbRootDropTargetId()
    : getDocumentsTableBreadcrumbFolderDropTargetId(folderId);

const getBreadcrumbDropTargetTestId = (folderId: string | null) =>
  `documents-breadcrumb-drop-target-${folderId ?? "root"}`;

const DocumentsBreadcrumbDropTarget = ({
  children,
  disabled,
  folderId,
  moveController,
}: DocumentsBreadcrumbDropTargetProps) => {
  const moveTarget = getBreadcrumbMoveTarget(folderId);
  const hasActiveDrag = Boolean(moveController.activeDragItemId);
  const canDrop =
    !hasActiveDrag ||
    Boolean(
      buildDocumentsTableMovePlan({
        foldersById: moveController.foldersById,
        itemIds: moveController.activeDragSubjectItemIds,
        itemsById: moveController.itemsById,
        target: moveTarget,
      })
    );
  const { isOver, setNodeRef } = useDroppable({
    id: getBreadcrumbDropTargetId(folderId),
    disabled: disabled || !canDrop,
  });
  const isActiveDropTarget = hasActiveDrag && isOver && canDrop;

  return (
    <Box
      component="span"
      data-testid={getBreadcrumbDropTargetTestId(folderId)}
      ref={setNodeRef}
      sx={{
        alignItems: "center",
        backgroundColor: isActiveDropTarget ? "#DBEAFE" : "transparent",
        borderRadius: 1,
        display: "inline-flex",
        px: 0.5,
        py: 0.25,
        transition: "background-color 120ms ease",
      }}
    >
      {children}
    </Box>
  );
};

export const DocumentsPageBreadcrumb = ({
  disabled = false,
  isLoading = false,
  items,
  moveController,
}: DocumentsPageBreadcrumbProps) => {
  if (isLoading) {
    return <Skeleton variant="rounded" height={24} width={220} />;
  }

  return (
    <Breadcrumbs aria-label="breadcrumb">
      {items.map((item, index) => {
        const content = item.to ? (
          <Link
            color="inherit"
            component={RouterLink}
            to={item.to}
            underline="hover"
          >
            {item.label}
          </Link>
        ) : (
          <Typography component="span">{item.label}</Typography>
        );

        if (item.folderId !== undefined) {
          return (
            <DocumentsBreadcrumbDropTarget
              disabled={disabled}
              folderId={item.folderId}
              key={index}
              moveController={moveController}
            >
              {content}
            </DocumentsBreadcrumbDropTarget>
          );
        }

        return <span key={index}>{content}</span>;
      })}
    </Breadcrumbs>
  );
};
